import PropTypes from 'prop-types';
import React, { memo, useCallback, useMemo, useState } from 'react';

import { DefaultAutoComplete } from '../../ui-kit';
import * as autoCompleteComponents from '../../ui-kit/autoCompleteField/components';
import CreateModuleModal from '../createModuleModal';

const ModuleMenuList = ({ children, onCreate, ...props }) => (
  <autoCompleteComponents.MenuList {...props}>
    {children}
    <div
      className="module-select__create-option"
      onClick={(e) => {
        e.stopPropagation();
        onCreate();
      }}
      onKeyPress={(e) => {
        e.stopPropagation();
        onCreate();
      }}
      role="button"
      tabIndex="0"
    >
      + Create
    </div>
  </autoCompleteComponents.MenuList>
);

ModuleMenuList.propTypes = {
  children: PropTypes.node.isRequired,
  onCreate: PropTypes.func.isRequired,
};

const ModuleSelect = ({
  options,
  value,
  onChange,
  isDisabled,
  placeholder,
  error,
  touched,
  isPending,
  projectId,
  onCreateSuccess,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleCreateSuccess = useCallback((module) => {
    setIsModalOpen(false);
    if (onCreateSuccess) {
      onCreateSuccess(module);
    }
  }, [onCreateSuccess]);

  const handleCreate = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const components = useMemo(() => ({
    MenuList: function ModuleMenuListWrapper(menuListProps) {
      return (
        <ModuleMenuList
          {...menuListProps}
          onCreate={handleCreate}
        />
      );
    },
  }), [handleCreate]);

  return (
    <>
      <DefaultAutoComplete
        name="project_module_id"
        options={options}
        value={value}
        onChange={onChange}
        isDisabled={isDisabled}
        placeholder={placeholder}
        error={error}
        touched={touched}
        closeMenuOnSelect
        components={components}
      />
      {isModalOpen && (
        <CreateModuleModal
          isOpen={isModalOpen}
          onClose={handleClose}
          projectId={projectId}
          onSuccess={handleCreateSuccess}
          isPending={isPending}
        />
      )}
    </>
  );
};

ModuleSelect.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  })).isRequired,
  value: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  onChange: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  touched: PropTypes.bool,
  isPending: PropTypes.bool,
  projectId: PropTypes.number.isRequired,
  onCreateSuccess: PropTypes.func,
};

ModuleSelect.defaultProps = {
  value: null,
  isDisabled: false,
  placeholder: 'Choose module',
  error: '',
  touched: false,
  isPending: false,
  onCreateSuccess: null,
};

export default memo(ModuleSelect);
